'use client';

import { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { cn, isModifiedClick, formatTokenCompact, truncateAddress } from '@/lib/utils';
import { lobbyBoardIdFromGridId, lobbyHrefForBoard } from '@/lib/lobby-routes';
import { useProfileModal } from '@/contexts/profile-modal-context';
import { resolveRewardTokenInfo } from '@/lib/rewards';
import { Zap, Skull, Flag, LogOut, Coins, Gavel } from 'lucide-react';
import { BoardroomIcon } from '@/components/shared';
import { HV_TAKEOVER_GRID_ID, isHiddenV21Grid } from '@/lib/constants/addresses';
import {
  useGlobalActivity,
  type GlobalActivityItem,
} from '@/hooks/use-global-activity';
import { useDemoActivityOptional } from '@/contexts/demo-activity-context';
import { useEthPrice } from '@/hooks/use-eth-price';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { AvatarWithFlair, LevelBadge } from '@/components/prelaunch/avatar-with-flair';
import { resolveIdentity, type IdentityInfo } from '@/components/shared/avatar-tooltip';

// ─── Compact amount formatter for ticker pills ─────────────────────────────

function formatTickerAmount(rawAmount: string, decimals: number, prefix = '$', suffix = ''): string {
  const num = Number(rawAmount) / 10 ** decimals;
  const abs = Math.abs(num);
  let formatted: string;
  if (abs >= 1_000_000_000) formatted = `${prefix}${Math.round(abs / 1_000_000_000)}B`;
  else if (abs >= 1_000_000) formatted = `${prefix}${Math.round(abs / 1_000_000)}M`;
  else if (abs >= 1_000) formatted = `${prefix}${Math.round(abs / 1_000)}K`;
  else if (abs >= 100) formatted = `${prefix}${Math.round(abs)}`;
  else if (abs >= 0.01) formatted = `${prefix}${abs.toFixed(2)}`;
  else if (abs > 0) formatted = `<${prefix}0.01`;
  else formatted = `${prefix}0`;
  return suffix ? `${formatted} ${suffix}` : formatted;
}

// ─── Ticker item (simplified view of a GlobalActivityItem) ──────────────────

export type TickerEventType =
  | 'BUY'
  | 'AUCTION_BUY'
  | 'SEAT_CLAIMED'
  | 'MINT'
  | 'FORFEIT'
  | 'ABANDON'
  | 'LAUNCH'
  | 'GRID_LAUNCHED'
  | 'FEES_CLAIMED'
  | 'TAX_CLAIMED'
  | 'OTHER';

export interface TickerItem {
  id: string;
  userAddress: string;
  userName?: string;
  userAvatar?: string;
  userLevel?: number;
  coinSymbol?: string;
  coinImageUrl?: string;
  coinAddress?: string;
  poolId: string;
  gridId?: string;
  eventType: TickerEventType;
  price?: string;
  amount?: string;
  yieldTokenAddress?: string;
  yieldTokenSymbol?: string;
  yieldTokenDecimals?: number;
  seatCount?: number;
  isTakeover?: boolean;
}

// Map API event types to simplified ticker types
function mapEventType(raw: string, isPreGraduation?: boolean): TickerEventType {
  switch (raw) {
    case 'BOUGHT':
      return isPreGraduation ? 'MINT' : 'BUY';
    case 'AUCTION_BUY':
      return 'AUCTION_BUY';
    case 'CLAIMED':
      return isPreGraduation ? 'MINT' : 'SEAT_CLAIMED';
    case 'FORFEITED':
      return 'FORFEIT';
    case 'LAUNCHED':
      return 'LAUNCH';
    case 'ABANDONED':
      return 'ABANDON';
    case 'FEES_CLAIMED':
      return 'FEES_CLAIMED';
    case 'TAX_CLAIMED':
      return 'TAX_CLAIMED';
    case 'GRID_LAUNCHED':
      return 'GRID_LAUNCHED';
    default:
      return 'OTHER';
  }
}

function toTickerItem(item: GlobalActivityItem): TickerItem {
  const evType = mapEventType(item.eventType, item.isPreGraduation);
  return {
    id: item.id,
    userAddress: item.user,
    userName: item.userIdentity?.displayName,
    userAvatar: item.userIdentity?.avatarUrl,
    userLevel: item.userIdentity?.level,
    coinSymbol: item.coinSymbol || item.coinName,
    coinImageUrl: item.coinImageUrl,
    coinAddress: item.coinAddress,
    poolId: item.poolId,
    gridId: item.gridId,
    eventType: evType,
    price: item.totalPrice || item.price,
    amount: item.amount,
    yieldTokenAddress: item.yieldTokenAddress,
    yieldTokenSymbol: item.yieldTokenSymbol,
    yieldTokenDecimals: item.yieldTokenDecimals,
    seatCount: item.seatCount,
    isTakeover: item.isTakeover,
  };
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/**
 * Per-event accent pack. Drives the event badge text colour, the pill's
 * left-edge accent bar, and the pill's subtle background tint so every
 * event reads as a distinct "chip" at a glance — not a wall of uniform
 * amber pills. The label tone stays bright against the dark pill body
 * (WCAG AA vs #0a0907), and the accent bar doubles as a progress-indicator
 * style cue when several events of the same class stream in at once.
 *
 * `tone` covers the three style props we need per event:
 *   • `label` — the text colour for the icon + label combo in the pill.
 *   • `rail`  — the 2px left-edge accent strip at the pill's start.
 *   • `tint`  — an optional very soft background wash that sits over the
 *               shared pill body so the eye can cluster like events
 *               without the chip becoming noisy.
 */
interface EventTone {
  label: string;
  rail: string;
  tint: string;
}

const EVENT_TONES: Record<TickerEventType, EventTone> = {
  BUY: { label: 'text-rose-300', rail: 'bg-rose-400', tint: 'bg-rose-500/5' },
  AUCTION_BUY: { label: 'text-rose-300', rail: 'bg-rose-400', tint: 'bg-rose-500/5' },
  SEAT_CLAIMED: { label: 'text-emerald-300', rail: 'bg-emerald-400', tint: 'bg-emerald-500/5' },
  MINT: { label: 'text-amber-300', rail: 'bg-amber-400', tint: 'bg-amber-500/5' },
  FORFEIT: { label: 'text-red-300', rail: 'bg-red-500', tint: 'bg-red-500/5' },
  ABANDON: { label: 'text-orange-300', rail: 'bg-orange-400', tint: 'bg-orange-500/5' },
  LAUNCH: { label: 'text-sky-300', rail: 'bg-sky-400', tint: 'bg-sky-500/5' },
  FEES_CLAIMED: { label: 'text-sky-300', rail: 'bg-sky-400', tint: 'bg-sky-500/5' },
  TAX_CLAIMED: { label: 'text-amber-300', rail: 'bg-amber-400', tint: 'bg-amber-500/5' },
  GRID_LAUNCHED: { label: 'text-sky-300', rail: 'bg-sky-400', tint: 'bg-sky-500/5' },
  OTHER: { label: 'text-zinc-300', rail: 'bg-zinc-500', tint: 'bg-zinc-500/5' },
};

function eventToneFor(type: TickerEventType): EventTone {
  return EVENT_TONES[type] ?? EVENT_TONES.OTHER;
}

function EventBadge({ type }: { type: TickerEventType }) {
  const tone = eventToneFor(type);
  const cls = cn('inline-flex items-center gap-0.5 font-semibold tracking-tight', tone.label);
  switch (type) {
    case 'BUY':
      return (
        <span className={cls}>
          <Zap className="h-2.5 w-2.5" />
          Buy
        </span>
      );
    case 'AUCTION_BUY':
      return (
        <span className={cls}>
          <Gavel className="h-2.5 w-2.5" />
          Auction Buy
        </span>
      );
    case 'SEAT_CLAIMED':
      return (
        <span className={cls}>
          <Flag className="h-2.5 w-2.5" />
          List
        </span>
      );
    case 'MINT':
      return (
        <span className={cls}>
          <Zap className="h-2.5 w-2.5" />
          Mint
        </span>
      );
    case 'FORFEIT':
      return (
        <span className={cls}>
          <Skull className="h-2.5 w-2.5" />
          Forfeit
        </span>
      );
    case 'ABANDON':
      return (
        <span className={cls}>
          <LogOut className="h-2.5 w-2.5" />
          Abandoned
        </span>
      );
    case 'LAUNCH':
      return (
        <span className={cls}>
          <Zap className="h-2.5 w-2.5" />
          Launch
        </span>
      );
    case 'FEES_CLAIMED':
      return (
        <span className={cls}>
          <Coins className="h-2.5 w-2.5" />
          Claim
        </span>
      );
    case 'TAX_CLAIMED':
      return (
        <span className={cls}>
          <Coins className="h-2.5 w-2.5" />
          Tax
        </span>
      );
    case 'GRID_LAUNCHED':
      return (
        <span className={cls}>
          <Zap className="h-2.5 w-2.5" />
          Launched
        </span>
      );
    default:
      return null;
  }
}

function formatClaimAmount(item: TickerItem, ethPrice: number): string | null {
  if (!item.amount) return null;
  const info = resolveRewardTokenInfo(
    item.yieldTokenAddress,
    item.yieldTokenDecimals ?? 18,
    ethPrice,
    undefined,
    item.yieldTokenSymbol,
  );
  const human = Number(item.amount) / 10 ** info.decimals;
  if (info.kind === 'usd') {
    const usd = human * info.priceUsd;
    if (usd > 0 && usd < 0.01) return '<$0.01';
    if (usd >= 1_000_000_000) return `$${Math.round(usd / 1e9)}B`;
    if (usd >= 1_000_000) return `$${Math.round(usd / 1e6)}M`;
    if (usd >= 1_000) return `$${Math.round(usd / 1e3)}K`;
    if (usd >= 100) return `$${Math.round(usd)}`;
    return `$${usd.toFixed(2)}`;
  }
  return formatTokenCompact(human, info.symbol);
}

/** Base pill styling. Event-specific accent (left rail + soft tint) is
 *  layered in by `TickerActivityItem`, so the shared wrapper only sets
 *  the body surface, border, and text baseline.
 *
 *  Shape polish: the pills now sit on a slightly taller, more angular
 *  chip with a 3px radius to fit the gilded-UI language used elsewhere
 *  in the app. The previous `rounded-[5px] px-2.5 py-[3px]` reads as a
 *  generic badge — lowering the radius and nudging the horizontal
 *  padding turns it into a proper holographic tag. */
const PILL_VARIANTS = {
  default:
    'bg-card/85 border border-border/40 text-foreground/90',
  lobby:
    'bg-[#0b0a08]/75 border border-amber-500/20 backdrop-blur-[2px] shadow-[inset_0_0_0_1px_rgba(255,215,120,0.04)] text-amber-50 hover:border-amber-400/40 transition-colors',
} as const;

function TickerActivityItem({
  item,
  isNew,
  ethPrice,
  onProfileOpen,
  onBoardSelect,
  variant = 'default',
}: {
  item: TickerItem;
  isNew: boolean;
  ethPrice: number;
  onProfileOpen?: (address: string) => void;
  /** Navigate to `/board/…` without a full document load. */
  onBoardSelect?: (lobbyBoardId: string) => void;
  variant?: 'default' | 'lobby';
}) {
  const level = item.userLevel ?? 0;
  const id = item.userAddress
    ? resolveIdentity(item.userAddress, { displayName: item.userName, avatarUrl: item.userAvatar, level }, {})
    : null;

  const isBoardroomEvent = item.gridId === HV_TAKEOVER_GRID_ID;

  const displaySymbol = isBoardroomEvent ? 'Boardroom' : item.coinSymbol;

  const showCoinLink =
    !!displaySymbol
    && (item.eventType !== 'TAX_CLAIMED' || isBoardroomEvent)
    && !!item.gridId;

  const coreContent = (
    <>
      {item.userAddress && id && (
        <button
          type="button"
          className="shrink-0 rounded-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onProfileOpen?.(item.userAddress)}
          title="View profile"
        >
          <AvatarWithFlair avatarUrl={id.avatarUrl} address={item.userAddress} level={id.level} size="xxs" />
        </button>
      )}

      <EventBadge type={item.eventType} />

      {item.price && item.eventType !== 'FORFEIT' && item.eventType !== 'ABANDON' && item.eventType !== 'FEES_CLAIMED' && item.eventType !== 'TAX_CLAIMED' && (
        <span
          className={cn(
            'font-mono',
            item.eventType === 'SEAT_CLAIMED' ? 'text-emerald-400' : 'text-muted-foreground',
          )}
        >
          {formatTickerAmount(item.price, 6)}
        </span>
      )}

      {(item.eventType === 'FEES_CLAIMED' || item.eventType === 'TAX_CLAIMED') && (() => {
        const display = formatClaimAmount(item, ethPrice);
        return display ? (
          <span
            className={cn(
              'font-mono',
              item.eventType === 'TAX_CLAIMED' ? 'text-amber-400' : 'text-accent-blue',
            )}
          >
            {display}
          </span>
        ) : null;
      })()}

      {item.isTakeover && item.seatCount && (
        <span className="font-bold text-amber-400">{item.seatCount} seats</span>
      )}

      {item.eventType === 'MINT' && item.seatCount && item.seatCount > 1 && (
        <span className="font-medium text-amber-400">&times;{item.seatCount}</span>
      )}
    </>
  );

  const coinIcon = isBoardroomEvent ? (
    <BoardroomIcon size="sm" />
  ) : item.coinImageUrl ? (
    <img
      src={item.coinImageUrl}
      alt={item.coinSymbol}
      className="h-3.5 w-3.5 rounded-full"
    />
  ) : null;

  const lobbyId = item.gridId ? lobbyBoardIdFromGridId(item.gridId) : null;

  const coinLink = showCoinLink && lobbyId && onBoardSelect ? (
    <button
      type="button"
      className="inline-flex items-center gap-0.5 text-muted-foreground hover:text-foreground transition-colors"
      onClick={(e) => {
        if (isModifiedClick(e)) return;
        e.preventDefault();
        onBoardSelect(lobbyId);
      }}
    >
      {coinIcon}
      <span className="font-medium">{displaySymbol}</span>
    </button>
  ) : showCoinLink && lobbyId ? (
    <Link
      href={lobbyHrefForBoard(lobbyId)}
      className="inline-flex items-center gap-0.5 text-muted-foreground hover:text-foreground transition-colors"
      scroll={false}
    >
      {coinIcon}
      <span className="font-medium">{displaySymbol}</span>
    </Link>
  ) : null;

  const hasTooltip = item.userAddress && id;
  const tone = eventToneFor(item.eventType);

  return (
    <div
      data-item-id={item.id}
      className={cn(
        'relative inline-flex items-center gap-1.5 px-2 pr-2 py-[3px] rounded-[3px] overflow-hidden',
        PILL_VARIANTS[variant],
        'text-[11px] whitespace-nowrap shrink-0',
        isNew && 'opacity-0',
      )}
    >
      {/* Soft tint only — left accent rail removed per lobby polish. */}
      <span aria-hidden className={cn('absolute inset-0 pointer-events-none', tone.tint)} />
      <span className="relative inline-flex items-center gap-1.5">
      {hasTooltip ? (
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5">
              {coreContent}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="p-2">
            <div className="flex items-start gap-3 py-1 min-w-[180px]">
              <div className="flex flex-col items-center shrink-0">
                <AvatarWithFlair avatarUrl={id.avatarUrl} address={item.userAddress} level={id.level} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  className="font-medium truncate block text-left hover:underline hover:text-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onProfileOpen?.(item.userAddress);
                  }}
                >
                  {id.displayName || truncateAddress(item.userAddress)}
                </button>
                {id.level > 0 && <LevelBadge level={id.level} className="text-[10px]" />}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        coreContent
      )}
      {coinLink}
      </span>
    </div>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const GAP = 8;
const SLIDE_DURATION = 500;
const GLOW_DURATION = 800;

const COIN_DEMO_PATH = /^\/coin\/0x[a-fA-F0-9]{40}\/demo$/;

// ─── Main component ─────────────────────────────────────────────────────────

interface GlobalActivityTickerProps {
  /**
   * 'default' (the AppShell ticker above Header) keeps the standard
   * background/border treatment so it blends into the desktop chrome.
   * 'lobby' restyles the wrapper for the gilded dark surface used inside
   * `/`, where the ticker sits between LobbyNav and the board canvas.
   *
   * Only the wrapper background/border/blur changes between variants; the
   * inner pills and animation logic are identical.
   */
  variant?: 'default' | 'lobby';
}

const WRAPPER_VARIANTS = {
  default: 'bg-background/90 border-b border-border/30 backdrop-blur-sm',
  lobby:
    'bg-gradient-to-b from-[#0a0907]/95 to-[#050404]/95 border-b border-amber-500/25 backdrop-blur-md shadow-[0_10px_40px_-30px_rgba(255,215,120,0.4)]',
} as const;

export function GlobalActivityTicker({ variant = 'default' }: GlobalActivityTickerProps = {}) {
  const pathname = usePathname();
  const router = useRouter();
  const isCoinDemo = pathname ? COIN_DEMO_PATH.test(pathname) : false;
  const demoActivity = useDemoActivityOptional();
  const { items: rawItems, isLoading, injectTestItem } = useGlobalActivity(50, {
    enabled: !isCoinDemo,
  });
  const { openProfile } = useProfileModal();
  const { ethPrice } = useEthPrice();
  const [displayItems, setDisplayItems] = useState<TickerItem[]>([]);
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isAnimating = useRef(false);
  const pendingItems = useRef<TickerItem[] | null>(null);
  const pendingNewIds = useRef<Set<string>>(new Set());
  const prevIdsRef = useRef<Set<string>>(new Set());
  /** First visible ticker item after mapEventType filter — skip slide/glow on poll if unchanged. */
  const committedHeadIdRef = useRef<string>('');
  const cleanupTimer = useRef<ReturnType<typeof setTimeout>>();

  // Coin demo route: drive ticker purely from synthetic items (no global API poll)
  useEffect(() => {
    if (!isCoinDemo || !demoActivity) return;
    const tickerItems = demoActivity.items;
    if (tickerItems.length === 0) {
      setDisplayItems([]);
      prevIdsRef.current = new Set();
      committedHeadIdRef.current = '';
      return;
    }
    const headId = tickerItems[0]?.id ?? '';
    if (prevIdsRef.current.size === 0) {
      setDisplayItems(tickerItems);
      prevIdsRef.current = new Set(tickerItems.map((t) => t.id));
      committedHeadIdRef.current = headId;
      return;
    }
    const newItems = tickerItems.filter((t) => !prevIdsRef.current.has(t.id));
    const idsUnchanged =
      newItems.length === 0 && prevIdsRef.current.size === tickerItems.length;
    prevIdsRef.current = new Set(tickerItems.map((t) => t.id));
    if (idsUnchanged) return;

    const headUnchanged =
      headId !== '' &&
      headId === committedHeadIdRef.current;
    const shouldAnimateNews =
      newItems.length > 0 && !headUnchanged;

    if (isAnimating.current) {
      pendingItems.current = tickerItems;
      if (shouldAnimateNews) {
        for (const item of newItems) pendingNewIds.current.add(item.id);
      }
      return;
    }

    if (newItems.length === 0) {
      setDisplayItems(tickerItems);
      committedHeadIdRef.current = headId;
      return;
    }

    setDisplayItems(tickerItems);
    committedHeadIdRef.current = headId;
    if (!shouldAnimateNews) return;

    isAnimating.current = true;
    setAnimatingIds(new Set(newItems.map((t) => t.id)));
  }, [isCoinDemo, demoActivity, demoActivity?.version]);

  // Convert API items to ticker items and detect changes
  useEffect(() => {
    if (isCoinDemo) return;
    if (rawItems.length === 0) return;

    const tickerItems = rawItems
      .filter((i) => {
        if (!i.gridId || isHiddenV21Grid(i.gridId)) return false;
        const t = mapEventType(i.eventType, i.isPreGraduation);
        return t !== 'OTHER';
      })
      .map(toTickerItem);

    const headId = tickerItems[0]?.id ?? '';

    // First load — set items without animation
    if (prevIdsRef.current.size === 0) {
      setDisplayItems(tickerItems);
      prevIdsRef.current = new Set(tickerItems.map((t) => t.id));
      committedHeadIdRef.current = headId;
      return;
    }

    const newItems = tickerItems.filter(
      (t) => !prevIdsRef.current.has(t.id)
    );

    const idsUnchanged = newItems.length === 0
      && prevIdsRef.current.size === tickerItems.length;

    prevIdsRef.current = new Set(tickerItems.map((t) => t.id));

    if (idsUnchanged) return;

    const headUnchanged =
      headId !== '' &&
      headId === committedHeadIdRef.current;
    const shouldAnimateNews =
      newItems.length > 0 && !headUnchanged;

    if (isAnimating.current) {
      pendingItems.current = tickerItems;
      if (shouldAnimateNews) {
        for (const item of newItems) pendingNewIds.current.add(item.id);
      }
      return;
    }

    if (newItems.length === 0) {
      setDisplayItems(tickerItems);
      committedHeadIdRef.current = headId;
      return;
    }

    setDisplayItems(tickerItems);
    committedHeadIdRef.current = headId;
    if (!shouldAnimateNews) return;

    isAnimating.current = true;
    setAnimatingIds(new Set(newItems.map((t) => t.id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, isCoinDemo]);

  // Flush buffered items and start animation for any pending new IDs
  const flushPending = useCallback(() => {
    if (pendingItems.current) {
      const next = pendingItems.current;
      setDisplayItems(next);
      committedHeadIdRef.current = next[0]?.id ?? '';
      pendingItems.current = null;
    }
    if (pendingNewIds.current.size > 0) {
      const ids = pendingNewIds.current;
      pendingNewIds.current = new Set();
      isAnimating.current = true;
      setAnimatingIds(ids);
    }
  }, []);

  // Run batch FLIP slide + glow when animatingIds changes
  useEffect(() => {
    if (animatingIds.size === 0) return;

    const track = trackRef.current;
    const scroller = scrollRef.current;
    if (!track) return;

    const isAtStart = !scroller || scroller.scrollLeft < 10;

    // Find all new elements and measure total width
    const newEls: HTMLElement[] = [];
    let totalWidth = 0;
    for (const child of Array.from(track.children)) {
      const el = child as HTMLElement;
      if (animatingIds.has(el.dataset.itemId || '')) {
        newEls.push(el);
        totalWidth += el.offsetWidth;
      }
    }

    if (newEls.length === 0) {
      isAnimating.current = false;
      flushPending();
      return;
    }

    if (isAtStart) {
      const totalOffset = totalWidth + (newEls.length * GAP);
      track.style.transition = 'none';
      track.style.transform = `translateX(-${totalOffset}px)`;
    }

    // Schedule the animated transform + glow on the next paint frame.
    // The previous implementation read `track.offsetHeight` between
    // the two `transform` writes to force a synchronous layout flush,
    // which surfaced in Chrome's performance trace as a recurring
    // "forced reflow while executing JavaScript" insight on every
    // burst of new ticker items (the activity feed batches several
    // events per second during high-traffic boards). A double-rAF
    // achieves the same FROM/TO commit boundary as the offsetHeight
    // read — the browser is guaranteed to have laid out the
    // `transition: none` + reset-transform pair before the second
    // frame runs — without a layout-thrashing read.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isAtStart) {
          track.style.transition = `transform ${SLIDE_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`;
          track.style.transform = 'translateX(0)';
        }

        for (const el of newEls) {
          if (!isAtStart) {
            el.style.opacity = '1';
          }

          el.style.transition = `opacity ${SLIDE_DURATION * 0.5}ms ease, box-shadow ${GLOW_DURATION}ms ease-out`;
          el.style.opacity = '1';
          el.style.boxShadow = '0 0 12px 2px rgba(255, 255, 255, 0.3), inset 0 0 8px rgba(255, 255, 255, 0.05)';

          setTimeout(() => {
            el.style.boxShadow = '';
          }, GLOW_DURATION * 0.35);
        }
      });
    });

    const cleanup = isAtStart ? Math.max(SLIDE_DURATION, GLOW_DURATION) + 100 : GLOW_DURATION + 100;
    cleanupTimer.current = setTimeout(() => {
      setAnimatingIds(new Set());
      isAnimating.current = false;
      if (track) {
        track.style.transition = '';
        track.style.transform = '';
      }
      flushPending();
    }, cleanup);

    return () => {
      if (cleanupTimer.current) clearTimeout(cleanupTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animatingIds]);

  // ── Dev shortcut: G to inject a test item ──────────────────────────────────
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        const testItem: GlobalActivityItem = {
          id: `test-${Date.now()}`,
          eventType: ['BOUGHT', 'AUCTION_BUY', 'CLAIMED', 'FORFEITED'][Math.floor(Math.random() * 4)],
          poolId: '0x0',
          user: `0x${Math.random().toString(16).slice(2, 42).padEnd(40, '0')}`,
          timestamp: Math.floor(Date.now() / 1000),
          txHash: `0x${Math.random().toString(16).slice(2)}`,
          coinSymbol: ['DEGEN', 'CHAD', 'MOON', 'PEPE', 'APE'][Math.floor(Math.random() * 5)],
          price: (Math.floor(Math.random() * 200 + 5) * 1_000_000).toString(),
        };
        injectTestItem(testItem);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [injectTestItem]);

  // ── Click-and-drag scrolling ─────────────────────────────────────────────
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = scroller.scrollLeft;
    // Don't capture pointer here — capturing immediately steals events from
    // child <Link> elements, preventing clicks from working. We defer capture
    // to pointerMove once actual drag movement is detected.
    scroller.style.cursor = 'grabbing';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 3) {
      hasDragged.current = true;
      // Capture pointer only once we know the user is dragging, not clicking
      const scroller = scrollRef.current;
      if (scroller && !scroller.hasPointerCapture(e.pointerId)) {
        scroller.setPointerCapture(e.pointerId);
        scroller.style.userSelect = 'none';
      }
    }
    const scroller = scrollRef.current;
    if (scroller) {
      scroller.scrollLeft = dragScrollLeft.current - dx;
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const scroller = scrollRef.current;
    if (scroller) {
      if (scroller.hasPointerCapture(e.pointerId)) {
        scroller.releasePointerCapture(e.pointerId);
      }
      scroller.style.userSelect = '';
      scroller.style.cursor = '';
    }
  }, []);

  // Suppress link clicks when the user was dragging
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDragged.current = false;
    }
  }, []);

  const wrapperClass = WRAPPER_VARIANTS[variant];

  if (isCoinDemo && displayItems.length === 0) {
    return (
      <div className={cn('relative z-40 w-full overflow-hidden', wrapperClass)}>
        <div className="flex items-center gap-2 px-3 py-1.5 w-full">
          <span className="text-[11px] text-muted-foreground">Demo mode — use keyboard shortcuts on the grid</span>
        </div>
      </div>
    );
  }

  if ((!isCoinDemo && isLoading) || (!isCoinDemo && displayItems.length === 0)) {
    return (
      <div className={cn('relative z-40 w-full overflow-hidden', wrapperClass)}>
        <div className="flex items-center gap-2 px-3 py-1 w-full">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 min-w-0 items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/40 animate-pulse"
              style={{ height: 28 }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={scrollRef}
        className={cn('relative z-40 w-full overflow-x-auto scrollbar-hide', wrapperClass)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClickCapture={handleClickCapture}
      >
        <div
          ref={trackRef}
          className="flex items-center gap-2 px-3 py-1 will-change-transform"
        >
          {displayItems.map((item) => (
            <TickerActivityItem
              key={item.id}
              item={item}
              isNew={animatingIds.has(item.id)}
              ethPrice={ethPrice}
              variant={variant}
              onProfileOpen={(addr) => openProfile(addr)}
              onBoardSelect={(lobbyBoardId) => {
                /**
                 * Mirror `LobbyClient`'s `setActiveBoardId`: `router.prefetch`
                 * warms the route, and `startTransition` hands the push to
                 * React's scheduler so it doesn't get dropped when the click
                 * originates inside a subtree with Suspense / data hooks
                 * (see the long comment in `LobbyClient.tsx` — same bug).
                 * Without this wrapper, clicks from the global header ticker
                 * sometimes no-op and the board never switches.
                 */
                const path = lobbyHrefForBoard(lobbyBoardId);
                router.prefetch(path);
                startTransition(() => {
                  router.push(path, { scroll: false });
                });
              }}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
